import React from "react"
import { NoExperiencesModal, DeactivateDeleteModal, RESOURCE_TYPES } from "ui/DeactivateDeleteModals"
import { EQUIPMENT_DELETE_DEACTIVATE_MODES } from "../../../constants"
import PropTypes from "prop-types"

/**
 * DeactivateDeleteModals component renders the appropriate modal based on mode
 * @param {object} modalState - Modal state from useEquipmentModals hook
 * @param {Function} onClose - Close modal handler
 * @param {Function} onConfirm - Confirm action handler
 * @param {boolean} deactivateLoading - Deactivate action loading state
 * @param {boolean} deleteLoading - Delete action loading state
 * @param {boolean} deactivationDetailsLoading - Deactivation details loading state
 * @param {boolean} deletionDetailsLoading - Deletion details loading state
 * @param {Function} t - Translation function
 */
const DeactivateDeleteModals = ({
  modalState,
  onClose,
  onConfirm,
  deactivateLoading,
  deleteLoading,
  deactivationDetailsLoading,
  deletionDetailsLoading,
  t
}) => {
  if (!modalState?.visible) return null

  const equipmentTitle = modalState?.equipment?.title || ""
  const mode = modalState?.mode
  const details = modalState?.details || {}
  const action = modalState?.action
  const expMessage = details.exp_message || null
  const bookingMessage = details.booking_message || null

  const getWarningMessage = () => {
    if (mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE) {
      return null
    }
    if (expMessage) return expMessage
    if (bookingMessage) return bookingMessage
    if (action === "deactivate") {
      return t("equipment.modals.warning_deactivate_remove", { name: equipmentTitle })
    }
    return t("equipment.modals.warning_delete_remove", { name: equipmentTitle })
  }

  const equipmentName = details.boat_name || equipmentTitle
  const assignedTripsCount = details.assigned_trips?.length || 0
  const activeBookingsCount = details.active_bookings?.length || 0

  // Generate subtitle based on action and mode
  const getSubtitle = () => {
    if (action === "deactivate") {
      switch (mode) {
        case EQUIPMENT_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
          return (
            <>
              <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} {assignedTripsCount}{" "}
              {t("equipment.modals.experiences")}:
            </>
          )
        case EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
        case EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
          return (
            <>
              <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} ({activeBookingsCount}){" "}
              {t("equipment.modals.upcoming_trips")}. {t("equipment.modals.to_deactivate")} <strong>{equipmentName}</strong>{" "}
              {t("equipment.modals.you_must_replace_it")}.
            </>
          )
        default:
          return (
            <>
              {t("equipment.modals.deactivate_equipment_description_prefix")}
              <strong>{equipmentName}</strong>
              {t("equipment.modals.deactivate_equipment_description_suffix")}
            </>
          )
      }
    } else if (action === "delete") {
      switch (mode) {
        case EQUIPMENT_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
          return (
            <>
              <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} {assignedTripsCount}{" "}
              {t("equipment.modals.experiences")}:
            </>
          )
        case EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
        case EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
          return (
            <>
              <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} ({activeBookingsCount}){" "}
              {t("equipment.modals.upcoming_trips")}. {t("equipment.modals.to_delete")} <strong>{equipmentName}</strong>{" "}
              {t("equipment.modals.you_must_replace_it")}.
            </>
          )
        default:
          return (
            <>
              {t("equipment.modals.delete_equipment_description_prefix")}
              <strong>{equipmentName}</strong>
              {t("equipment.modals.delete_equipment_description_suffix")}
            </>
          )
      }
    }
    return null
  }

  const isNoExperiencesMode = mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES
  const isDeactivateAction = action === "deactivate"
  const actionLoading = isDeactivateAction ? deactivateLoading : deleteLoading
  const detailsLoadingState = isDeactivateAction ? deactivationDetailsLoading : deletionDetailsLoading
  const combinedLoading = actionLoading || detailsLoadingState

  // Render NoExperiences modal for simple cases
  if (isNoExperiencesMode) {
    return (
      <NoExperiencesModal
        open={modalState.visible}
        title={isDeactivateAction ? t("equipment.modals.deactivate_equipment_title") : t("equipment.modals.delete_equipment_title")}
        description={
          isDeactivateAction
            ? t("equipment.modals.deactivate_equipment_description", { title: equipmentTitle })
            : t("equipment.modals.delete_equipment_description", { title: equipmentTitle })
        }
        onCancel={onClose}
        onOk={onConfirm}
        okText={isDeactivateAction ? t("global.deactivate") : t("global.delete")}
        cancelText={t("global.cancel")}
        loading={actionLoading}
      />
    )
  }

  // Render DeleteDeactivate modal for complex cases (with mode)
  if (mode) {
    return (
      <DeactivateDeleteModal
        visible={modalState.visible}
        onCancel={onClose}
        onOk={onConfirm}
        title={isDeactivateAction ? t("equipment.modals.deactivate_equipment_title") : t("equipment.modals.delete_equipment_title")}
        subtitle={getSubtitle()}
        okText={isDeactivateAction ? t("global.deactivate") : t("global.delete")}
        cancelText={t("global.cancel")}
        loading={combinedLoading}
        mode={mode}
        list={details.assigned_trips || []}
        upcomingBookings={details.active_bookings || []}
        assignedExperiences={details.assigned_trips || []}
        warning={getWarningMessage()}
        expMessage={expMessage}
        bookingMessage={bookingMessage}
        resourceType={RESOURCE_TYPES.EQUIPMENT}
        translationPrefix="equipment"
      />
    )
  }

  return null
}

DeactivateDeleteModals.propTypes = {
  modalState: PropTypes.shape({
    visible: PropTypes.bool,
    mode: PropTypes.oneOf(Object.values(EQUIPMENT_DELETE_DEACTIVATE_MODES)),
    equipment: PropTypes.shape({
      title: PropTypes.string
      // Add other equipment properties you use
    }),
    details: PropTypes.shape({
      exp_message: PropTypes.string,
      booking_message: PropTypes.string
      // Add other detail properties you use
    }),
    action: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  deactivateLoading: PropTypes.bool,
  deleteLoading: PropTypes.bool,
  deactivationDetailsLoading: PropTypes.bool,
  deletionDetailsLoading: PropTypes.bool,
  t: PropTypes.func.isRequired
}

DeactivateDeleteModals.defaultProps = {
  modalState: null,
  deactivateLoading: false,
  deleteLoading: false,
  deactivationDetailsLoading: false,
  deletionDetailsLoading: false
}

export default DeactivateDeleteModals
